import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
	Alert,
	Badge,
	Box,
	Button,
	Divider,
	Group,
	NumberInput,
	Paper,
	SimpleGrid,
	Stack,
	Switch,
	Text,
	Title,
} from '@mantine/core'
import CreditCard from 'tabler-icons-react/dist/icons/credit-card'
import DeviceFloppy from 'tabler-icons-react/dist/icons/device-floppy'
import ExternalLink from 'tabler-icons-react/dist/icons/external-link'
import Refresh from 'tabler-icons-react/dist/icons/refresh'
import {
	createOrgCommerceStripeDashboardLink,
	createOrgCommerceStripeOnboardingLink,
	getOrgCommerceSettings,
	refreshOrgCommerceStripeStatus,
	updateOrgCommerceSettings,
} from '../services/actions/orgActions'

const defaultForm = {
	customerFeeEnabled: false,
	customerFeePercent: 0,
	customerFeeFixedDollars: 0,
}

const statusDetails = {
	ENABLED: {
		color: 'teal',
		label: 'Ready',
		description: 'Stripe can accept card payments and send payouts.',
	},
	PENDING: {
		color: 'yellow',
		label: 'Onboarding',
		description: 'Stripe setup has started and needs to be completed.',
	},
	RESTRICTED: {
		color: 'orange',
		label: 'Needs action',
		description: 'Stripe needs more information before commerce is ready.',
	},
	DISABLED: {
		color: 'red',
		label: 'Disabled',
		description: 'Stripe has disabled this connected account.',
	},
	NOT_STARTED: {
		color: 'gray',
		label: 'Not started',
		description: 'Create a Stripe connected account to enable checkout.',
	},
}

const centsToDollars = (value) => Number(value || 0) / 100
const dollarsToCents = (value) => Math.round(Number(value || 0) * 100)
const bpsToPercent = (value) => Number((Number(value || 0) / 100).toFixed(2))
const percentToBps = (value) => Math.round(Number(value || 0) * 100)

const formatMoney = (cents) =>
	new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(centsToDollars(cents))

const toNumber = (value, fallback = 0) => {
	const numeric = Number(value)
	return Number.isFinite(numeric) ? numeric : fallback
}

const formFromSettings = (settings = {}) => ({
	customerFeeEnabled: Boolean(settings.customerFeeEnabled),
	customerFeePercent: bpsToPercent(settings.customerFeeBps),
	customerFeeFixedDollars: centsToDollars(settings.customerFeeFixedCents),
})

const AdminCommercePage = () => {
	const { orgSlug } = useParams()
	const location = useLocation()
	const navigate = useNavigate()
	const [payload, setPayload] = useState(null)
	const [form, setForm] = useState(defaultForm)
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [startingOnboarding, setStartingOnboarding] = useState(false)
	const [refreshingStatus, setRefreshingStatus] = useState(false)
	const [openingDashboard, setOpeningDashboard] = useState(false)
	const [error, setError] = useState('')
	const [notice, setNotice] = useState('')

	const applyPayload = useCallback((nextPayload) => {
		setPayload(nextPayload)
		setForm(formFromSettings(nextPayload?.settings || {}))
	}, [])

	const loadSettings = useCallback(async () => {
		setLoading(true)
		setError('')
		try {
			const data = await getOrgCommerceSettings(orgSlug)
			applyPayload(data)
		} catch (err) {
			console.error('Failed to load commerce settings:', err)
			setError(err.message || 'Failed to load commerce settings.')
		} finally {
			setLoading(false)
		}
	}, [applyPayload, orgSlug])

	const refreshStatus = useCallback(
		async ({ message = 'Stripe commerce status refreshed.' } = {}) => {
			setRefreshingStatus(true)
			setError('')
			try {
				const data = await refreshOrgCommerceStripeStatus(orgSlug)
				applyPayload(data)
				setNotice(message)
			} catch (err) {
				console.error('Failed to refresh commerce Stripe status:', err)
				setError(err.message || 'Failed to refresh Stripe commerce status.')
			} finally {
				setRefreshingStatus(false)
			}
		},
		[applyPayload, orgSlug],
	)

	const startOnboarding = useCallback(async () => {
		setStartingOnboarding(true)
		setError('')
		setNotice('')
		try {
			const currentPageUrl = `${window.location.origin}${window.location.pathname}`
			const data = await createOrgCommerceStripeOnboardingLink(orgSlug, {
				returnUrl: `${currentPageUrl}?commerceOnboarding=return`,
				refreshUrl: `${currentPageUrl}?commerceOnboarding=refresh`,
			})
			applyPayload(data)
			const onboardingUrl = data?.accountLink?.url
			if (!onboardingUrl) {
				throw new Error('Stripe did not return an onboarding URL.')
			}
			window.location.assign(onboardingUrl)
		} catch (err) {
			console.error('Failed to start Stripe commerce onboarding:', err)
			setError(err.message || 'Failed to start Stripe onboarding.')
			setStartingOnboarding(false)
		}
	}, [applyPayload, orgSlug])

	useEffect(() => {
		loadSettings()
	}, [loadSettings])

	useEffect(() => {
		const params = new URLSearchParams(location.search)
		const onboardingState = params.get('commerceOnboarding')
		if (!onboardingState) return

		params.delete('commerceOnboarding')
		const nextSearch = params.toString()
		navigate(
			{
				pathname: location.pathname,
				search: nextSearch ? `?${nextSearch}` : '',
			},
			{ replace: true },
		)

		if (onboardingState === 'refresh') {
			startOnboarding()
			return
		}

		if (onboardingState === 'return') {
			refreshStatus({
				message: 'Stripe onboarding returned. Status refreshed.',
			})
		}
	}, [
		location.pathname,
		location.search,
		navigate,
		refreshStatus,
		startOnboarding,
	])

	const updateForm = (field, value) => {
		setForm((current) => ({
			...current,
			[field]: value,
		}))
	}

	const saveSettings = async () => {
		setSaving(true)
		setError('')
		setNotice('')
		try {
			const updatePayload = {
				customerFeeEnabled: Boolean(form.customerFeeEnabled),
				customerFeeBps: percentToBps(form.customerFeePercent),
				customerFeeFixedCents: dollarsToCents(form.customerFeeFixedDollars),
			}

			const data = await updateOrgCommerceSettings(orgSlug, updatePayload)
			applyPayload(data)
			setNotice(data?.message || 'Commerce settings saved.')
		} catch (err) {
			console.error('Failed to save commerce settings:', err)
			setError(err.message || 'Failed to save commerce settings.')
		} finally {
			setSaving(false)
		}
	}

	const openStripeDashboard = async () => {
		setOpeningDashboard(true)
		setError('')
		setNotice('')
		try {
			const data = await createOrgCommerceStripeDashboardLink(orgSlug)
			applyPayload(data)
			const dashboardUrl = data?.loginLink?.url
			if (!dashboardUrl) {
				throw new Error('Stripe did not return a dashboard URL.')
			}
			window.location.assign(dashboardUrl)
		} catch (err) {
			console.error('Failed to open Stripe commerce dashboard:', err)
			setError(err.message || 'Failed to open Stripe dashboard.')
			setOpeningDashboard(false)
		}
	}

	if (loading) {
		return (
			<Box px='md' py='md'>
				Loading commerce settings...
			</Box>
		)
	}

	const settings = payload?.settings || {}
	const status = settings.onboardingStatus || 'NOT_STARTED'
	const statusInfo = statusDetails[status] || statusDetails.NOT_STARTED
	const hasStripeAccount = Boolean(settings.stripeConnectedAccountId)
	const stripePaymentsReady =
		hasStripeAccount &&
		status === 'ENABLED' &&
		Boolean(settings.stripeChargesEnabled) &&
		Boolean(settings.stripePayoutsEnabled)
	const showRefreshStatus = hasStripeAccount && !stripePaymentsReady

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='xs'>
				Commerce
			</Title>
			<Text c='dimmed' mb='md'>
				{payload?.org?.name || orgSlug}
			</Text>

			{error && (
				<Alert color='red' mb='sm'>
					{error}
				</Alert>
			)}
			{notice && (
				<Alert color='teal' mb='sm'>
					{notice}
				</Alert>
			)}

			<Stack spacing='md'>
				<Paper withBorder p='md' radius='md'>
					<Group position='apart' align='flex-start' mb='md'>
						<div>
							<Group spacing='xs'>
								<Title order={4}>Stripe Payments</Title>
								<Badge color={statusInfo.color} variant='light'>
									{statusInfo.label}
								</Badge>
							</Group>
							<Text size='sm' c='dimmed' mt={4}>
								{statusInfo.description}
							</Text>
						</div>
						{!stripePaymentsReady && (
							<Group>
								{showRefreshStatus && (
									<Button
										variant='light'
										leftIcon={<Refresh size={16} />}
										onClick={() => refreshStatus()}
										loading={refreshingStatus}
										disabled={startingOnboarding}
									>
										Refresh Status
									</Button>
								)}
								<Button
									leftIcon={<CreditCard size={16} />}
									onClick={startOnboarding}
									loading={startingOnboarding}
								>
									{hasStripeAccount ? 'Continue Onboarding' : 'Start Onboarding'}
								</Button>
							</Group>
						)}
					</Group>

					<SimpleGrid
						cols={3}
						breakpoints={[
							{ maxWidth: 'md', cols: 1 },
						]}
					>
						<Box>
							<Text size='sm' c='dimmed'>
								Connected account
							</Text>
							<Text fw={600}>
								{settings.stripeConnectedAccountId || 'Not created'}
							</Text>
						</Box>
						<Box>
							<Text size='sm' c='dimmed'>
								Card charges
							</Text>
							<Badge
								color={settings.stripeChargesEnabled ? 'teal' : 'gray'}
								variant='light'
							>
								{settings.stripeChargesEnabled ? 'Enabled' : 'Not enabled'}
							</Badge>
						</Box>
						<Box>
							<Text size='sm' c='dimmed'>
								Payouts
							</Text>
							<Badge
								color={settings.stripePayoutsEnabled ? 'teal' : 'gray'}
								variant='light'
							>
								{settings.stripePayoutsEnabled ? 'Enabled' : 'Not enabled'}
							</Badge>
						</Box>
					</SimpleGrid>

					<Divider my='md' />

					<Group position='apart'>
						<Text size='sm' c='dimmed'>
							Use the Stripe dashboard to manage payout details after onboarding.
						</Text>
						<Button
							variant='light'
							leftIcon={<ExternalLink size={16} />}
							onClick={openStripeDashboard}
							loading={openingDashboard}
							disabled={!hasStripeAccount}
						>
							Open Stripe Dashboard
						</Button>
					</Group>
				</Paper>

				<Paper withBorder p='md' radius='md'>
					<Group position='apart' align='flex-start' mb='md'>
						<div>
							<Title order={4}>Checkout Settings</Title>
							<Text size='sm' c='dimmed'>
								These values affect on-site check-in checkout sessions.
							</Text>
						</div>
						<Button
							leftIcon={<DeviceFloppy size={16} />}
							onClick={saveSettings}
							loading={saving}
						>
							Save Settings
						</Button>
					</Group>

					<Stack spacing='md'>
						<Switch
							label='Customer-paid checkout fee'
							description='Adds this fee on top of the item price during checkout.'
							checked={form.customerFeeEnabled}
							onChange={(event) =>
								updateForm('customerFeeEnabled', event.currentTarget.checked)
							}
						/>

						<SimpleGrid
							cols={2}
							breakpoints={[
								{ maxWidth: 'md', cols: 1 },
							]}
						>
							<NumberInput
								label='Customer fee percent'
								value={form.customerFeePercent}
								onChange={(value) =>
									updateForm('customerFeePercent', toNumber(value))
								}
								min={0}
								max={100}
								precision={2}
								step={0.25}
								disabled={!form.customerFeeEnabled}
							/>
							<NumberInput
								label='Customer fixed fee'
								value={form.customerFeeFixedDollars}
								onChange={(value) =>
									updateForm('customerFeeFixedDollars', toNumber(value))
								}
								min={0}
								precision={2}
								step={0.25}
								disabled={!form.customerFeeEnabled}
							/>
						</SimpleGrid>

						<Text size='sm' c='dimmed'>
							Current customer fee: {form.customerFeeEnabled
								? `${toNumber(form.customerFeePercent).toFixed(2)}% + ${formatMoney(
										dollarsToCents(form.customerFeeFixedDollars),
								  )}`
								: 'None'}
						</Text>

					</Stack>
				</Paper>
			</Stack>
		</Box>
	)
}

export default AdminCommercePage
